
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { saveWebsiteCredentials, getWebsiteCredentials, type WordPressCredentials } from "@/services/wordpressService";

// Form schema for WordPress credentials
const formSchema = z.object({
  siteUrl: z.string().url("Please enter a valid URL"),
  username: z.string().min(1, "WordPress username is required"),
  password: z.string().min(1, "WordPress password is required"),
});

interface EditWebsiteCredentialsDialogProps {
  websiteId: string;
  websiteTitle: string;
  websiteUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EditWebsiteCredentialsDialog = ({
  websiteId,
  websiteTitle,
  websiteUrl,
  isOpen,
  onClose,
}: EditWebsiteCredentialsDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);

  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      siteUrl: websiteUrl,
      username: "",
      password: "",
    },
  });

  // Load existing credentials when dialog opens
  const loadExistingCredentials = async () => {
    if (!websiteId) return;
    
    setIsLoadingCredentials(true);
    try {
      console.log("Loading credentials for website:", websiteId);
      const credentials = await getWebsiteCredentials(websiteId);
      if (credentials) {
        console.log("Loaded credentials:", { siteUrl: credentials.siteUrl, username: credentials.username });
        form.setValue("siteUrl", credentials.siteUrl);
        form.setValue("username", credentials.username);
        // Don't pre-fill password for security reasons
      } else {
        console.log("No existing credentials found for website:", websiteId);
      }
    } catch (error) {
      console.error("Error loading credentials:", error);
      toast({
        title: "Error loading credentials",
        description: "There was an error loading existing credentials.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCredentials(false);
    }
  };

  // Load credentials when dialog opens and websiteId changes
  useEffect(() => {
    if (isOpen && websiteId) {
      loadExistingCredentials();
    }
  }, [isOpen, websiteId]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    console.log("Saving credentials for website:", websiteId, values);
    
    try {
      const credentials: WordPressCredentials = {
        siteUrl: values.siteUrl,
        username: values.username,
        password: values.password,
      };

      await saveWebsiteCredentials(websiteId, credentials);
      
      toast({
        title: "WordPress credentials saved",
        description: `Credentials for ${websiteTitle} have been saved successfully to the database.`,
      });
      
      // Reset form and close dialog
      form.reset();
      onClose();
    } catch (error) {
      console.error("Error saving credentials:", error);
      toast({
        title: "Error saving credentials",
        description: "There was an error saving your WordPress credentials to the database. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit WordPress Credentials</DialogTitle>
          <DialogDescription>
            Configure WordPress connection details for {websiteTitle}
          </DialogDescription>
        </DialogHeader>
        
        {isLoadingCredentials ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading existing credentials...</div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="siteUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WordPress Site URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://your-wordpress-site.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the full URL of your WordPress site
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WordPress Admin Username</FormLabel>
                    <FormControl>
                      <Input placeholder="admin" {...field} />
                    </FormControl>
                    <FormDescription>
                      Administrator username with permission to manage backups
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WordPress Admin Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="•••••••••" {...field} />
                    </FormControl>
                    <FormDescription>
                      Your password is securely encrypted and stored in the database
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save Credentials"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
